import React, { useEffect, useState } from 'react'
import { Button } from 'react-bootstrap';
import Select from "react-select"
import { Loader } from './Loader';
import { IconExclamationCircle, IconPencil } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PostRequest } from '../../Utils/hooks/ApiHandler';
import { baseURL } from '../../Utils/constants/constant';

export const ImportStep2 = ({
    data,
    customer,
    headers,
    extras,
    loading,
    setLoading,
    files,
    values,
    fileHeaders,
    setUnmappedCount,
    currentSelected,
    setCurrStep,
    setImportResult,
    mapping,
    setMapping
}) => {
    const navigate = useNavigate();

    const [fieldGroups, setFieldGroups] = useState([]);

    const [errors, setErrors] = useState({});

    const customSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: 38,
            fontSize: 13,
            padding: "0 4px",
            boxShadow: "none",
            border: state.isFocused ? "1px solid #2684FF" : "1px solid #d1d5db",
            "&:hover": {
                borderColor: "#60a5fa",
            },
        }),

        valueContainer: (base) => ({
            ...base,
            padding: "0 4px",
        }),

        input: (base) => ({
            ...base,
            padding: "2px 0",
            margin: 0,
        }),

        placeholder: (base) => ({
            ...base,
            fontSize: 13,
        }),

        singleValue: (base) => ({
            ...base,
            fontSize: 13,
        }),

        indicatorsContainer: (base) => ({
            ...base,
            height: 34,
            padding: "0 3px",
        }),

        dropdownIndicator: (base) => ({
            ...base,
            padding: "4px",
        }),

        option: (base) => ({
            ...base,
            fontSize: 13,
            padding: "6px 10px",
        }),

        menuList: (base) => ({
            ...base,
            maxHeight: 200,
            overflowY: "auto",
            scrollBehavior: "smooth",
            "::-webkit-scrollbar": {
                width: "8px",
            },
            "::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
            },
            "::-webkit-scrollbar-thumb": {
                background: "#c9c9c9",
                borderRadius: "4px",
            },
            "::-webkit-scrollbar-thumb:hover": {
                background: "#888",
            },
        }),

        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),

        menu: (base) => ({
            ...base,
            zIndex: 9999,
        }),

        multiValue: (base) => ({
            ...base,
            backgroundColor: "#3b82f6",
            color: "white",
            fontSize: 12,
        }),

        multiValueLabel: (base) => ({
            ...base,
            color: "white",
            fontWeight: 500,
        }),

        multiValueRemove: (base) => ({
            ...base,
            color: "white",
            ":hover": {
                backgroundColor: "#ef4444",
                color: "white",
            },
        }),
    };

    useEffect(() => {
        if (data === null) return;
        setFieldGroups(
            data?.fieldData.map((c) => ({
                key: c.title?.toLowerCase()?.split(" ")?.join("_"),
                title: c.title,
                fields: c.fields,
            }))
        );
    }, []);

    const handleChange = (groupKey, field, selected) => {
        setMapping((prev) => ({
            ...prev,
            [groupKey]: {
                ...(prev[groupKey] || {}),
                [field]: selected,
            },
        }));
    };

    const getSelectedValues = () => {
        return Object.values(mapping)
            .flatMap((group) => Object.values(group || {}))
            ?.filter(Boolean)
            ?.map((item) => item.value);
    };

    const getFilteredOptions = () => {
        const selectedValues = getSelectedValues();

        return fileHeaders?.filter(
            (option) => !selectedValues.includes(option.value)
        );
    };



    const handleUploadLabels = async () => {
        setLoading(true);
        try {
            // Validate required fields before proceeding
            const newErrors = {};
            let firstErrorFieldId = null;

            for (const group of fieldGroups) {
                for (const field of group.fields) {
                    if (field.required) {
                        const selected = mapping[group.key]?.[field.id];
                        if (!selected) {
                            if (!newErrors[group.key]) newErrors[group.key] = {};
                            newErrors[group.key][field.id] = true;

                            if (!firstErrorFieldId) {
                                firstErrorFieldId = `${group.key}-${field.id}`;
                            }
                        }
                    }
                }
            }

            setErrors(newErrors);

            if (firstErrorFieldId) {
                const el = document.getElementById(firstErrorFieldId);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                }
                return;
            }

            if (!data.module_name) return;
            const labels = Object.values(mapping).flatMap((group) =>
                Object.entries(group || {}).map(([field, selected]) => ({
                    default_label: field,
                    mapped_label: selected?.label || null,
                }))
            );

            const allFieldIds = fieldGroups.flatMap((g) => g.fields.map((f) => f.id));

            setUnmappedCount(
                allFieldIds?.filter((id) => !labels.some((l) => l.default_label === id))
                    .length
            );

            const finalLabels = labels;

            const payload = {
                customer_id: customer.customer_details?.customer_id,
                user_login_id: customer.customer_details?.id,
                action:
                    data?.module_name === "contact" || data?.module_name === "company"
                        ? currentSelected
                        : "insert",
                module_name: data?.module_name,
                labels: finalLabels,
                values: values,
            };
            const res = await PostRequest(
                `${baseURL}/csv/verifyImportingData/`,
                payload,
                false,
                { headers },
                extras
            );
            setImportResult(res);
            return res;
        } catch (error) {
            console.error("Upload labels failed", error);
        } finally {
            setLoading(false);
        }
    };
    return (
        <>
            {loading ? (
                <Loader />
            ) : (
                <div
                    className="mx-auto"
                    style={{ maxWidth: "850px", paddingBottom: "2rem" }}
                >
                    {/* Context Header */}
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <span className="fs-14 text-muted">
                            Selected file:{" "}
                            <strong className="text-primary">{files[0].name}</strong>
                        </span>
                        <span className="badge bg-light text-dark border fw-normal">
                            Step 2 of 3
                        </span>
                    </div>

                    {/* Info Alert - Soft Blue */}
                    <div
                        className="d-flex align-items-center gap-2 mb-4 p-3"
                        style={{
                            backgroundColor: "#f0f7ff",
                            borderLeft: "4px solid #007bff",
                            borderRadius: "4px",
                            color: "#2c5282",
                        }}
                    >
                        <IconExclamationCircle size={18} />
                        <span className="fs-14 fw-500">
                            The best match for each field has been automatically
                            selected based on your column headers.
                        </span>
                    </div>

                    {/* Data Format Configuration - Minimalist */}
                    <div className="mb-4 p-3 border rounded shadow-sm bg-white">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6
                                className="mb-0 fs-14 fw-bold text-uppercase text-muted"
                                style={{ letterSpacing: "0.5px" }}
                            >
                                Default Data Formats
                            </h6>
                            <button className="btn btn-link btn-sm text-decoration-none p-0 fs-13">
                                <IconPencil size={14} className="me-1" /> Edit
                            </button>
                        </div>
                        <div className="d-flex gap-5">
                            <div>
                                <label className="d-block fs-12 text-muted mb-1">
                                    Date Format
                                </label>
                                <span className="fs-14 fw-500">
                                    Automatic (per field)
                                </span>
                            </div>
                            <div>
                                <label className="d-block fs-12 text-muted mb-1">
                                    Decimal Separator
                                </label>
                                <span className="fs-14 fw-500">
                                    Standard (123,456.78)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Mapping Sections */}
                    {fieldGroups.map((group) => (
                        <div key={group.key} className="mb-4">
                            {/* Group Title */}
                            <h6
                                className="fs-15 fw-bold mb-3"
                                style={{ color: "#334155" }}
                            >
                                {group.title}
                            </h6>

                            {/* Table Container */}
                            <div className="border rounded bg-white shadow-sm ">
                                {/* Mapping Table Header */}
                                <div
                                    className="d-flex bg-light p-2 border-bottom text-muted fw-bold"
                                    style={{ fontSize: "11px", letterSpacing: "0.05rem" }}
                                >
                                    <div style={{ width: "45%", paddingLeft: "10px" }}>
                                        SYSTEM FIELD
                                    </div>
                                    <div style={{ width: "55%", paddingLeft: "10px" }}>
                                        IMPORT AS COLUMN
                                    </div>
                                </div>

                                {/* Mapping Rows */}
                                {group.fields.map((field, index) => (
                                    <div
                                        key={field.id}
                                        id={`${group.key}-${field.id}`}
                                        className={`d-flex align-items-center p-2 ${index !== group.fields.length - 1
                                            ? "border-bottom"
                                            : ""
                                            }`}
                                        style={{ transition: "background-color 0.2s" }}
                                        onMouseEnter={(e) =>
                                            (e.currentTarget.style.backgroundColor = "#fcfcfc")
                                        }
                                        onMouseLeave={(e) =>
                                        (e.currentTarget.style.backgroundColor =
                                            "transparent")
                                        }
                                    >
                                        <div
                                            style={{ width: "45%", paddingLeft: "10px" }}
                                            className={`fs-14 ${field.required && "text-danger"
                                                } fw-500`}
                                        >
                                            {field.label}{" "}
                                            {field.required && (
                                                <span className="text-danger">*</span>
                                            )}
                                        </div>
                                        <div style={{ width: "55%" }}>
                                            <Select
                                                options={getFilteredOptions()}

                                                styles={customSelectStyles}
                                                value={mapping[group.key]?.[field.id] || null}
                                                onChange={(selected) => {
                                                    handleChange(group.key, field.id, selected);
                                                    setErrors((prev) => ({
                                                        ...prev,
                                                        [group.key]: {
                                                            ...(prev[group.key] || {}),
                                                            [field.id]: false,
                                                        },
                                                    }));
                                                }}
                                                isClearable
                                                placeholder="Choose column..."

                                            />
                                            {errors[group.key]?.[field.id] && (
                                                <p
                                                    style={{
                                                        color: "red",
                                                        fontSize: "12px",
                                                        margin: "4px 0 0 10px",
                                                    }}
                                                >
                                                    This field is required
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Navigation Footer */}
                    <div className="d-flex justify-content-between align-items-center mt-5 pt-4 border-top">
                        <Button
                            className="px-4 fs-14 btn btn-default"
                            onClick={() => setCurrStep(1)}
                        >
                            Previous Step
                        </Button>
                        <div className="d-flex gap-2">
                            <Button className="text-muted btn btn-default-outline text-decoration-none fs-14">
                                Cancel
                            </Button>
                            <Button
                                className="px-5 fs-14 fw-500 btn btn-default"
                                onClick={async () => {
                                    const result = await handleUploadLabels();
                                    if (result) {
                                        setImportResult(result);
                                        setCurrStep(3);
                                    }
                                }}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}